/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AutoComplete, Button, Input, Select } from 'antd';
import './ReceptionInfo.less';
import { useAuth } from '@isocel/front-common';

import { useStoreByOrganization } from '../../../queries/store';
import { PackagingFlowDirection, Store } from '../../../queries/api/types';
import { usePackagingFlow } from '../../../context/PackagingFlowContext';
import { getPackingFlowFromStore, validateTransportVoucher } from '../../../queries/api/packaging-flow';
import { useNotification } from '../../notification/Notification';
import { usePalettes } from '../../../queries/palette';

const { Option } = Select;

const ReceptionInfo: React.FC = () => {
    const [store, setStore] = useState<Store>({
        name: '',
        id: '',
        code: '',
        organization: '',
    });
    const [storeLabel, setStoreLabel] = useState('');
    const [options, setOptions] = useState<Store[]>([]);
    const { showNotification } = useNotification();
    const history = useHistory();
    const location = useLocation<{
        storeId?: string;
        storeName?: string;
        voucher?: string;
        parcName?: string;
        parcId?: string;
    }>();
    const {
        setSelectedStore,
        selectedParc,
        setSelectedParc,
        parcPalettes,
        setParcPalettes,
        setPackageFlow,
        voucher,
        setVoucher,
        setFlowType,
        reset,
    } = usePackagingFlow();
    const { user } = useAuth();
    const { data: palettesData } = usePalettes(user?.organization?.id || '');
    const { data: stores } = useStoreByOrganization(user?.organization?.id ?? '');

    useEffect(() => {
        reset();
        if (palettesData?.length === 1) {
            setSelectedParc(palettesData[0]);
        }
        setParcPalettes(palettesData || []);
    }, [setParcPalettes, palettesData]);

    // Prefill from navigation state (coming from dashboard Réceptionner dropdown)
    useEffect(() => {
        const state = location.state;
        if (!state) return;
        if (state.storeName && state.storeId) {
            setStoreLabel(state.storeName);
            setStore({ name: state.storeName, id: state.storeId, code: '', organization: '' });
        }
        if (state.voucher) {
            setVoucher(state.voucher);
        }
        if (state.parcName && parcPalettes.length > 0) {
            const parc = parcPalettes.find((p) => p.name === state.parcName) || null;
            if (parc) setSelectedParc(parc);
        }
    }, [location.state, parcPalettes]);

    const handleSearch = (value: string) => {
        const filtered = value
            ? (stores || []).filter(
                  (store) =>
                      store.name.toLowerCase().includes(value.toLowerCase()) ||
                      store.code.toLowerCase().includes(value.toLowerCase())
              )
            : stores || [];
        const sortedFiltered = filtered.sort((a, b) => {
            const numA = parseInt(a.code, 10);
            const numB = parseInt(b.code, 10);
            if (!isNaN(numA) && !isNaN(numB)) {
                return numA - numB;
            }
            return a.code.localeCompare(b.code);
        });
        setOptions(sortedFiltered.map((store) => ({ ...store, value: `${store.code} - ${store.name}` })));
    };

    const handleSelect = (value: string, option: Store) => {
        setStore({ ...store, name: value, id: option.id });
        setStoreLabel(value);
    };

    const handleClear = () => {
        setStore({ name: '', id: '', code: '', organization: '' });
        setStoreLabel('');
    };
    const handleVoucherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVoucher(e.target.value);
    };

    const handleParcChange = (value: string) => {
        const parc = parcPalettes.find((p) => p.name === value) || null;
        setSelectedParc(parc);
    };

    const sendRequest = async () => {
        try {
            const validateVoucher = await validateTransportVoucher(voucher, 3, store.id);
            if (validateVoucher.voucherFound === true) {
                showNotification(
                    'error',
                    'Erreur',
                    "Un flux de rendus d'emballages a déjà été créé pour ce bon de transport."
                );
                return;
            } else {
                setSelectedStore(store);
                setFlowType(PackagingFlowDirection.RecivedInOrganization);
                const fetchAndSetLoadingSessionsPackage = async (storeId: string, voucher: string) => {
                    try {
                        const response = await getPackingFlowFromStore(storeId, voucher);
                        if (response) {
                            setPackageFlow(response);
                            history.push(`/recipientMovement/${storeId}`);
                        } else {
                            setPackageFlow(null);
                            history.push(`/recipientTrack/${storeId}`);
                        }
                    } catch (error) {
                        history.push(`/recipientTrack/${storeId}`);
                        setPackageFlow(null);
                        console.error('Error fetching loading sessions packages:', error);
                    }
                };
                setVoucher(voucher);
                if (store.id && voucher) {
                    fetchAndSetLoadingSessionsPackage(store.id, voucher);
                }
            }
        } catch (error) {
            console.log('error', error);
        }
    };
    return (
        <>
            <div className="reception-info-container">
                <h2 className="reception-info-title">Veuillez saisir le magasin</h2>
                <form className="reception-info-form">
                    <div className="form-field">
                        <label htmlFor="recipientLabel">Libelle magasin</label>
                        <br />
                        <AutoComplete
                            id="recipientLabel"
                            className="w-full"
                            options={options}
                            value={storeLabel}
                            onSearch={(v) => {
                                setStoreLabel(v);
                                handleSearch(v);
                            }}
                            notFoundContent="Aucun résultat"
                            onSelect={handleSelect}
                            onChange={(value) => {
                                setStoreLabel(value);
                                if (!value) handleClear();
                            }}
                            placeholder="Tapez pour rechercher..."
                            onDropdownVisibleChange={(open) => {
                                if (open) {
                                    handleSearch('');
                                }
                            }}
                        />
                    </div>
                    <div className="form-field">
                        <label htmlFor="transportNumber">N° de bon de transport</label>
                        <br />
                        <Input
                            id="transportNumber"
                            placeholder="Exemple : 389911024"
                            className="input-field"
                            value={voucher}
                            onChange={handleVoucherChange}
                        />
                    </div>
                    <div className="form-field">
                        <label htmlFor="parcs">Parc palettes</label>
                        {palettesData?.length === 1 ? (
                            <Input value={palettesData[0].name} className="dropdown-input" />
                        ) : (
                            <Select
                                id="parcs"
                                placeholder="Choisir le parc palettes"
                                value={selectedParc?.name}
                                notFoundContent="Aucun résultat"
                                onChange={handleParcChange}
                                className="dropdown-input"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {parcPalettes.map((parc) => (
                                    <Option key={parc.id} value={parc.name}>
                                        {parc.name}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </div>
                    <div className="form-submit">
                        <Button
                            type="primary"
                            disabled={!store.name || !voucher || !selectedParc}
                            onClick={sendRequest}
                        >
                            Suivant
                        </Button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default ReceptionInfo;
